import { Fragment, useEffect, useState } from 'react';
import { api } from '../lib/api.ts';
import { formatDuration } from '../../shared/time.ts';
import { policz } from '../../shared/pl.ts';
import { Ladowanie, Odznaka, Pusto, Tabela, Zakladki, type Kolumna } from '../ui/index.tsx';

/**
 * Zarządzanie czasami normatywnymi.
 *
 * Normy są nasze, bo te z planowania zawierają błędy. Sam formularz do wpisywania
 * liczb by tu nie wystarczył — trzeba jeszcze wiedzieć, KTÓRE normy są złe.
 * Dlatego pierwszą zakładką jest konfrontacja normy z czasem rzeczywistym
 * z zarejestrowanej pracy, a nie lista do przewijania.
 */

interface Podejrzana {
  product_code: string; product_name: string | null;
  operation_code: string; source: string;
  norma_h: number; rzeczywisty_h: number; stosunek: number;
  pomiarow: number; ocena: 'za_ciasna' | 'za_luzna';
}
interface Analiza {
  progi: { minPomiarow: number; progCiasna: number; progLuzna: number };
  operacje: Array<{ operation_code: string; mediana_stosunku: number; kodow: number }>;
  podejrzane: Podejrzana[];
  zbadanych_par: number;
}
interface Norma {
  product_code: string; product_name: string | null; operation_code: string;
  operation_name: string; hours: number; source: string;
  hours_import: number | null; note: string | null; updated_at: string;
}
interface Brak {
  product_code: string; product_name: string | null; operation_code: string;
  operation_name: string; wpisow: number; ostatnia_praca: string;
}

type Widok = 'podejrzane' | 'przeglad' | 'braki' | 'historia';

export default function AdminCzasy() {
  const [widok, setWidok] = useState<Widok>('podejrzane');
  const [msg, setMsg] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <Zakladki<Widok>
        wartosc={widok}
        onZmiana={(w) => { setWidok(w); setMsg(null); }}
        opcje={[
          ['podejrzane', 'Podejrzane normy'],
          ['przeglad', 'Przeglądaj i edytuj'],
          ['braki', 'Brakujące normy'],
          ['historia', 'Historia zmian'],
        ]}
      />

      {msg && <p className="card p-3 text-body-sm text-plum-soft" role="status">{msg}</p>}

      {widok === 'podejrzane' && <Podejrzane onMsg={setMsg} />}
      {widok === 'przeglad' && <Przeglad onMsg={setMsg} />}
      {widok === 'braki' && <Braki />}
      {widok === 'historia' && <HistoriaZmian />}
    </div>
  );
}

// ---------------------------------------------------------------- podejrzane

function Podejrzane({ onMsg }: { onMsg: (s: string) => void }) {
  const [d, setD] = useState<Analiza | null>(null);
  const [laduje, setLaduje] = useState(true);

  const load = () => {
    setLaduje(true);
    api.get<Analiza>('/admin/czasy/analiza').then(setD).finally(() => setLaduje(false));
  };
  useEffect(load, []);

  if (laduje) return <Ladowanie tekst="Porównuję normy z zarejestrowaną pracą…" />;
  if (!d) return null;

  const systemowe = d.operacje.filter((o) => Math.abs(o.mediana_stosunku - 1) >= 0.15);

  return (
    <>
      <div className="card p-5">
        <h3 className="text-sub mb-1.5">Jak to jest liczone</h3>
        <p className="text-body-sm text-plum-soft">
          Dla każdej sztuki sumujemy czas <b>wszystkich</b> pracowników, którzy przy niej byli — bo dokładnie
          tyle opisuje norma. Porównujemy medianę, nie średnią, żeby jeden feralny dzień nie przewrócił wniosku.
          Pomijamy prototypy i pary z mniej niż {policz(d.progi.minPomiarow, 'pomiar')}.
          Zbadano {policz(d.zbadanych_par, 'para')} produkt-operacja.
        </p>
      </div>

      {systemowe.length > 0 && (
        <div className="card p-5 border-warn/30">
          <h3 className="text-sub mb-1">Przesunięcie systemowe całej operacji</h3>
          <p className="text-body-sm text-plum-soft mb-4">
            Gdy mediana dla całej operacji odbiega od 1, problem jest w normach, nie w ludziach.
            Przy normie za ciasnej pracownik nie osiągnie 100% nawet pracując wzorowo.
          </p>
          <div className="grid sm:grid-cols-3 gap-5">
            {systemowe.map((o) => {
              const proc = Math.round((o.mediana_stosunku - 1) * 100);
              return (
                <div key={o.operation_code}>
                  <div className="text-label uppercase text-plum-muted mb-2">{o.operation_code}</div>
                  <div className={`kpi ${proc > 0 ? 'text-bad' : 'text-warn'}`}>
                    {proc > 0 ? '+' : ''}{proc}%
                  </div>
                  <div className="text-caption text-plum-muted mt-2">
                    {proc > 0 ? 'dłużej niż norma' : 'krócej niż norma'} · {policz(o.kodow, 'kod')}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <span className="text-body-sm text-plum-soft">
          {policz(d.podejrzane.length, 'norma')} do sprawdzenia (próg: {d.progi.progCiasna}× i {d.progi.progLuzna}×)
        </span>
        <button className="btn-ghost ml-auto" onClick={load}>Przelicz</button>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="tbl">
            <thead>
              <tr>
                <th>Produkt</th>
                <th>Operacja</th>
                <th className="text-right">Norma</th>
                <th className="text-right">Rzeczywisty</th>
                <th className="text-right">Stosunek</th>
                <th className="text-right">Pomiarów</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {d.podejrzane.length === 0 && (
                <tr><td colSpan={7}>
                  <Pusto tytul="Brak podejrzanych norm"
                         opis="Albo normy są dobre, albo za mało danych — sprawdź licznik zbadanych par powyżej." />
                </td></tr>
              )}
              {d.podejrzane.map((p) => (
                <WierszPodejrzany key={`${p.product_code}|${p.operation_code}`} p={p} onMsg={onMsg} onZapis={load} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function WierszPodejrzany({ p, onMsg, onZapis }: { p: Podejrzana; onMsg: (s: string) => void; onZapis: () => void }) {
  const [edytuje, setEdytuje] = useState(false);
  const [wartosc, setWartosc] = useState(String(Math.round(p.rzeczywisty_h * 100) / 100));
  const [uwaga, setUwaga] = useState('');

  const zapisz = async () => {
    try {
      await api.patch('/admin/czasy', {
        product_code: p.product_code, operation_code: p.operation_code,
        hours: Number(wartosc.replace(',', '.')),
        note: uwaga.trim() || `korekta wg mediany z ${policz(p.pomiarow, 'pomiar')}`,
      });
      onMsg(`Zmieniono normę ${p.product_code} / ${p.operation_code} na ${wartosc} h.`);
      setEdytuje(false);
      onZapis();
    } catch (e: any) { onMsg(e.message); }
  };

  return (
    <Fragment>
      <tr>
        <td>
          <span className="block font-semibold tnum text-plum">{p.product_code}</span>
          <span className="block text-caption text-plum-muted truncate max-w-[280px]">{p.product_name}</span>
        </td>
        <td>{p.operation_code}</td>
        <td className="text-right tnum">{formatDuration(p.norma_h * 60)}</td>
        <td className="text-right tnum">{formatDuration(p.rzeczywisty_h * 60)}</td>
        <td className={`text-right tnum font-semibold ${p.ocena === 'za_ciasna' ? 'text-bad' : 'text-warn'}`}>
          {p.stosunek.toFixed(2)}×
          <span className="block text-caption font-normal text-plum-muted">
            {p.ocena === 'za_ciasna' ? 'norma za ciasna' : 'norma za luźna'}
          </span>
        </td>
        <td className="text-right tnum">{p.pomiarow}</td>
        <td className="text-right">
          <button className="btn-quiet !min-h-0 !py-1 !px-2 text-caption" aria-expanded={edytuje}
                  onClick={() => setEdytuje((v) => !v)}>
            {edytuje ? 'Anuluj' : 'Popraw'}
          </button>
        </td>
      </tr>
      {edytuje && (
        <tr className="bg-canvas">
          <td colSpan={7}>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <span className="label">Nowa norma (godziny)</span>
                <input className="field !w-32 tnum" value={wartosc} onChange={(e) => setWartosc(e.target.value)} />
              </div>
              <div className="flex-1 min-w-[200px]">
                <span className="label">Uzasadnienie</span>
                <input
                  className="field"
                  placeholder={`korekta wg mediany z ${policz(p.pomiarow, 'pomiar')}`}
                  value={uwaga}
                  onChange={(e) => setUwaga(e.target.value)}
                />
              </div>
              <button className="btn-primary" onClick={zapisz}>Zapisz normę</button>
            </div>
            <p className="text-caption text-plum-muted mt-2">
              Po zapisie norma zostaje oznaczona jako ręczna i kolejny import z planowania jej nie nadpisze.
            </p>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

// ---------------------------------------------------------------- przegląd

function Przeglad({ onMsg }: { onMsg: (s: string) => void }) {
  const [q, setQ] = useState('');
  const [filtr, setFiltr] = useState('');
  const [laduje, setLaduje] = useState(true);
  const [d, setD] = useState<{ czasy: Norma[]; licznik: { wszystkie: number; reczne: number }; limit: number } | null>(null);

  const load = () => {
    setLaduje(true);
    const p = new URLSearchParams();
    if (q.trim()) p.set('q', q.trim());
    if (filtr) p.set('filtr', filtr);
    api.get<{ czasy: Norma[]; licznik: { wszystkie: number; reczne: number }; limit: number }>(`/admin/czasy?${p}`)
      .then(setD).finally(() => setLaduje(false));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, filtr]);

  const przywroc = async (n: Norma) => {
    try {
      const r = await api.post<{ hours: number }>('/admin/czasy/przywroc', {
        product_code: n.product_code, operation_code: n.operation_code,
      });
      onMsg(`Przywrócono wartość z importu: ${r.hours} h.`);
      load();
    } catch (e: any) { onMsg(e.message); }
  };

  const kolumny: Array<Kolumna<Norma>> = [
    { klucz: 'produkt', naglowek: 'Produkt', sort: (n) => n.product_code,
      render: (n) => (
        <span>
          <span className="block font-semibold tnum text-plum">{n.product_code}</span>
          <span className="block text-caption text-plum-muted truncate max-w-[280px]">{n.product_name}</span>
        </span>
      ) },
    { klucz: 'operacja', naglowek: 'Operacja', sort: (n) => n.operation_name,
      render: (n) => n.operation_name },
    { klucz: 'norma', naglowek: 'Norma', prawo: true, sort: (n) => n.hours, szer: '96px',
      render: (n) => <span className="tnum font-semibold text-plum">{n.hours} h</span> },
    { klucz: 'import', naglowek: 'Z importu', prawo: true, szer: '96px',
      render: (n) => (
        <span className="tnum text-plum-muted">{n.hours_import !== null ? `${n.hours_import} h` : '—'}</span>
      ) },
    { klucz: 'zrodlo', naglowek: 'Źródło', sort: (n) => n.source, szer: '200px',
      render: (n) => (
        <span>
          {n.source === 'reczny' ? <Odznaka ton="info">ręczna</Odznaka> : <Odznaka>planowanie</Odznaka>}
          {n.note && <span className="block text-caption text-plum-muted truncate max-w-[220px]" title={n.note}>{n.note}</span>}
        </span>
      ) },
    { klucz: 'akcje', naglowek: '', prawo: true, szer: '150px',
      render: (n) => (
        n.source === 'reczny' && n.hours_import !== null ? (
          <button className="btn-quiet !min-h-0 !py-1 !px-2 text-caption hover:!text-bad" onClick={() => przywroc(n)}>
            Przywróć z importu
          </button>
        ) : null
      ) },
  ];

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <input className="field !w-auto flex-1 min-w-[220px]" placeholder="Szukaj po kodzie lub nazwie produktu"
               value={q} onChange={(e) => setQ(e.target.value)} aria-label="Szukaj normy" />
        <select className="field !w-auto" value={filtr} onChange={(e) => setFiltr(e.target.value)}
                aria-label="Filtr norm">
          <option value="">Wszystkie</option>
          <option value="reczne">Tylko korekty ręczne</option>
          <option value="rozbiezne">Ręczne różniące się od importu</option>
        </select>
        {d && (
          <span className="text-body-sm text-plum-soft">
            {policz(d.licznik.wszystkie, 'norma')}, w tym {d.licznik.reczne} ręcznych
          </span>
        )}
      </div>

      <Tabela
        dane={d?.czasy ?? []}
        kolumny={kolumny}
        klucz={(n) => `${n.product_code}|${n.operation_code}`}
        laduje={laduje}
        pusto={<Pusto tytul="Nic nie pasuje do filtra" opis="Zmień frazę wyszukiwania albo wybierz inny filtr." />}
      />

      {d && d.czasy.length >= d.limit && (
        <p className="text-caption text-plum-muted">
          Pokazano pierwsze {policz(d.limit, 'pozycja')} — zawęź wyszukiwanie, żeby zobaczyć resztę.
        </p>
      )}
    </>
  );
}

// ---------------------------------------------------------------- braki

function Braki() {
  const [braki, setBraki] = useState<Brak[]>([]);
  const [laduje, setLaduje] = useState(true);

  useEffect(() => {
    api.get<{ braki: Brak[] }>('/admin/czasy/braki')
      .then((r) => setBraki(r.braki)).finally(() => setLaduje(false));
  }, []);

  const kolumny: Array<Kolumna<Brak>> = [
    { klucz: 'produkt', naglowek: 'Produkt', sort: (b) => b.product_code,
      render: (b) => (
        <span>
          <span className="block font-semibold tnum text-plum">{b.product_code}</span>
          <span className="block text-caption text-plum-muted truncate max-w-[280px]">{b.product_name ?? '—'}</span>
        </span>
      ) },
    { klucz: 'operacja', naglowek: 'Operacja', sort: (b) => b.operation_name,
      render: (b) => b.operation_name },
    { klucz: 'wpisow', naglowek: 'Wpisów', prawo: true, sort: (b) => b.wpisow, szer: '96px',
      render: (b) => <span className="tnum">{b.wpisow}</span> },
    { klucz: 'ostatnia', naglowek: 'Ostatnia praca', prawo: true, sort: (b) => b.ostatnia_praca, szer: '130px',
      render: (b) => <span className="tnum text-plum-muted">{b.ostatnia_praca}</span> },
  ];

  return (
    <>
      <div className="card p-5">
        <h3 className="text-sub mb-1.5">Praca bez normy</h3>
        <p className="text-body-sm text-plum-soft">
          Produkty, na których zarejestrowano pracę, ale norma dla tej operacji nie istnieje.
          Ten czas wpada do mianownika efektywności, a nie wnosi nic do licznika — więc
          zaniża wynik pracownika bez jego winy. To pozycje do uzupełnienia w pierwszej kolejności.
        </p>
      </div>

      <Tabela
        dane={braki}
        kolumny={kolumny}
        klucz={(b) => `${b.product_code}|${b.operation_code}`}
        laduje={laduje}
        pusto={<Pusto tytul="Każda zarejestrowana praca ma przypisaną normę"
                      opis="Nic tu nie zalega — efektywność liczy się z kompletu danych." />}
      />
    </>
  );
}

// ---------------------------------------------------------------- historia zmian

interface Wpis {
  id: number; product_code: string; operation_code: string;
  hours_before: number | null; hours_after: number | null;
  source_before: string | null; source_after: string | null;
  reason: string | null; changed_at: string; login: string | null;
}

function HistoriaZmian() {
  const [h, setH] = useState<Wpis[]>([]);
  const [laduje, setLaduje] = useState(true);

  useEffect(() => {
    api.get<{ historia: Wpis[] }>('/admin/czasy/historia')
      .then((r) => setH(r.historia)).finally(() => setLaduje(false));
  }, []);

  const kolumny: Array<Kolumna<Wpis>> = [
    { klucz: 'kiedy', naglowek: 'Kiedy', sort: (w) => w.changed_at, szer: '170px',
      render: (w) => <span className="tnum whitespace-nowrap">{w.changed_at}</span> },
    { klucz: 'kto', naglowek: 'Kto', sort: (w) => w.login ?? '', szer: '90px',
      render: (w) => <span className="font-semibold tnum text-plum">{w.login ?? '—'}</span> },
    { klucz: 'co', naglowek: 'Produkt / operacja', sort: (w) => w.product_code,
      render: (w) => <span className="tnum">{w.product_code} / {w.operation_code}</span> },
    { klucz: 'zmiana', naglowek: 'Zmiana', prawo: true, szer: '160px',
      render: (w) => (
        <span className="tnum whitespace-nowrap">
          {w.hours_before ?? '—'} h → <b className="text-plum">{w.hours_after ?? '—'} h</b>
        </span>
      ) },
    { klucz: 'powod', naglowek: 'Uzasadnienie',
      render: (w) => <span className="text-plum-soft">{w.reason ?? '—'}</span> },
  ];

  return (
    <>
      <div className="card p-5">
        <p className="text-body-sm text-plum-soft">
          Normy sterują oceną ludzi, więc każda zmiana jest zapisana: kto, kiedy, z czego na co i dlaczego.
        </p>
      </div>

      <Tabela
        dane={h}
        kolumny={kolumny}
        klucz={(w) => w.id}
        laduje={laduje}
        pusto={<Pusto tytul="Żadna norma nie była jeszcze zmieniana"
                      opis="Wszystkie czasy pochodzą z importu z planowania." />}
      />
    </>
  );
}
